"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { useLanguage } from "./LanguageProvider";
import { ResponsiveVideo } from "./ResponsiveVideo";
import { BILSPEKTRUM_FILM } from "@/lib/bilspektrumVideos";

const EASE = [0.22, 1, 0.36, 1] as const;

/** Homepage band: book a workshop visit, over the Bilspektrum film (Oscar: no workshop footage). */
export function WorkshopCtaBand() {
  const { language } = useLanguage();
  const sv = language === "sv";

  return (
    <section className="relative overflow-hidden bg-black text-white">
      <ResponsiveVideo
        clip={BILSPEKTRUM_FILM}
        autoPlay
        loop
        preload="none"
        className="absolute inset-0 h-full w-full object-cover opacity-45"
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" aria-hidden />

      <div className="container relative mx-auto max-w-6xl px-6 py-20 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="max-w-xl"
        >
          <p className="mb-3 text-xs font-medium uppercase tracking-[0.25em] text-white/70">
            {sv ? "Verkstad" : "Workshop"}
          </p>
          <h2 className="mb-4 font-display text-3xl font-bold lg:text-5xl">
            {sv ? "Dags för service?" : "Time for a service?"}
          </h2>
          <p className="mb-8 text-white/80 lg:text-lg">
            {sv
              ? "Boka service och reparation online, direkt i vår verkstadskalender. Vi tar emot alla bilmärken."
              : "Book service and repairs online, straight into our workshop calendar. We service all car brands."}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              href="/boka-verkstad"
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-neutral-900 transition hover:gap-3"
            >
              {sv ? "Boka service online" : "Book service online"}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <a
              href="tel:090-120400"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold transition hover:bg-white/10"
            >
              <Phone className="h-4 w-4" aria-hidden />
              090-12 04 00
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
